import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toaster, { Theme } from '../../../../shared/toaster';
import {
  LeadRevealType,
  BULK_ACTION_TIMEOUT,
  LeadFinderSecondaryTab,
  PEOPLE_MAX_POLLING_LIMIT,
} from '../../types';
import {
  INSUFFICIENT_CREDITS_ERROR_MESSAGE,
  LEADS_PER_PAGE,
  SAVED_TAB_BULK_OPERATION_LIMIT,
  getV2SavedTabPageSize,
  isInsufficientCreditsError,
} from '../../helpers/helper';
import { leadFinderBulkOperationLimit } from '../../../../shared/utils/user-details';
import {
  bulkRevealLeadAPI,
  getLeadsV2,
  getSavedLeads,
} from '../../helpers/leads.api';
import { enableBulkActionRequestPolling } from '../../../home/home-slice';
import { SystemErrors } from '../../../../shared/enums/system-errors';
import { getPostLoadMetaRequest } from '../../../home/extra-actions';

interface UseV2LeadRevealProps {
  selectedLeads: number[];
  isSelectedAllLeads: boolean;
  deSelectedLeads: number[];
  leadsData: {
    profiles: any[];
    pagination: any;
  };
  handleGeneratePayload: () => any;
  setLeadsData: React.Dispatch<
    React.SetStateAction<{
      profiles: any[];
      pagination: any;
    }>
  >;
  setEmailRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  setEmailPhoneRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  emailRevealingLeads: Record<number, boolean>;
  emailPhoneRevealingLeads: Record<number, boolean>;
  setIsSelectedAllLeads: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  setDeSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  activeSubTab?: any;
  apiParam?: any;
  savedApiParam?: any;
  isLoading?: boolean;
}

export const useV2LeadReveal = ({
  selectedLeads,
  isSelectedAllLeads,
  deSelectedLeads,
  leadsData,
  handleGeneratePayload,
  setLeadsData,
  setEmailRevealingLeads,
  setEmailPhoneRevealingLeads,
  setIsSelectedAllLeads,
  setSelectedLeads,
  setDeSelectedLeads,
  activeSubTab,
  apiParam,
  savedApiParam,
  isLoading = false,
}: UseV2LeadRevealProps) => {
  const dispatch = useDispatch();
  const leadFinderLimit = leadFinderBulkOperationLimit();
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [isFirstPollRequest, setIsFirstPollRequest] = useState(true);
  const pollingAttemptsRef = useRef(0);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const hasRevealingLeadsRef = useRef(false);
  const lastPollingResponseRef = useRef<any>(null);
  const leadsDataRef = useRef(leadsData);
  const previousSubTabRef = useRef(activeSubTab);
  const pollingPageRef = useRef<number | null>(null);
  const pollingFiltersRef = useRef<string | null>(null);
  const isStartingPollingRef = useRef(false);
  const processedCombinationsRef = useRef<Set<string>>(new Set());
  const previousIsLoadingRef = useRef<boolean>(false);
  const [
    leadFinderRevealType,
    setLeadFinderRevealType,
  ] = useState<LeadRevealType>(LeadRevealType.EMAIL);
  const [
    selectAllRevealType,
    setSelectAllRevealType,
  ] = useState<LeadRevealType | null>(null);

  // Keep ref in sync with leadsData to avoid stale closures
  useEffect(() => {
    leadsDataRef.current = leadsData;
  }, [leadsData]);

  useEffect(() => {
    const previousTab = previousSubTabRef.current;
    previousSubTabRef.current = activeSubTab;

    if (previousTab && previousTab !== activeSubTab && isPollingEnabled) {
      setIsPollingEnabled(false);
      setEmailRevealingLeads({});
      setEmailPhoneRevealingLeads({});
      setIsSelectedAllLeads(false);
      setSelectedLeads([]);
      setDeSelectedLeads([]);
      setSelectAllRevealType(null);
      hasRevealingLeadsRef.current = false;
      pollingAttemptsRef.current = 0;
      lastPollingResponseRef.current = null;
      pollingPageRef.current = null;
      pollingFiltersRef.current = null;
      // Reset tracking so we can check the new tab
      processedCombinationsRef.current.clear();
      isStartingPollingRef.current = false;
    }
  }, [
    activeSubTab,
    isPollingEnabled,
    setIsPollingEnabled,
    setDeSelectedLeads,
    setEmailPhoneRevealingLeads,
    setEmailRevealingLeads,
    setIsSelectedAllLeads,
    setSelectedLeads,
    setSelectAllRevealType,
  ]);

  // Cancel polling when page or filters change
  useEffect(() => {
    if (
      isPollingEnabled &&
      pollingPageRef.current !== null &&
      pollingFiltersRef.current !== null
    ) {
      const filtersPayload = handleGeneratePayload();
      const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
      const currentPage = isSaved
        ? Math.ceil(
            (savedApiParam?.start || 1) /
              (savedApiParam?.take || getV2SavedTabPageSize()),
          )
        : apiParam?.page ?? 1;

      const currentFiltersStr = JSON.stringify(filtersPayload);
      const isPageMatch = currentPage === pollingPageRef.current;
      const isFilterMatch = currentFiltersStr === pollingFiltersRef.current;

      // Cancel polling if page or filters changed
      if (!isPageMatch || !isFilterMatch) {
        setIsPollingEnabled(false);
        setEmailRevealingLeads({});
        setEmailPhoneRevealingLeads({});
        setIsSelectedAllLeads(false);
        setSelectAllRevealType(null);
        hasRevealingLeadsRef.current = false;
        pollingAttemptsRef.current = 0;
        lastPollingResponseRef.current = null;
        pollingPageRef.current = null;
        pollingFiltersRef.current = null;
        // Reset tracking so we can check the new page/filter
        processedCombinationsRef.current.clear();
        isStartingPollingRef.current = false;
      }
    }
  }, [
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    setIsPollingEnabled,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    setIsSelectedAllLeads,
  ]);

  const handleFetchLeadsAfterBulkActions = useCallback(
    async (enablePolling = false, newPolling = false) => {
      try {
        if (newPolling) {
          const filtersPayload = handleGeneratePayload();
          const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
          const paginationPayload = isSaved ? savedApiParam : apiParam;
          const payload = {
            ...paginationPayload,
            ...filtersPayload,
          };

          // Store the polling context when starting a new poll request
          const pollingPage = isSaved
            ? Math.ceil(
                (savedApiParam?.start || 1) /
                  (savedApiParam?.take || getV2SavedTabPageSize()),
              )
            : apiParam?.page ?? 1;

          pollingPageRef.current = pollingPage;
          pollingFiltersRef.current = JSON.stringify(filtersPayload);

          // Call appropriate API based on active sub-tab
          let res;
          if (isSaved) {
            // For saved tab, use getSavedLeads API
            res = await getSavedLeads(payload);
          } else {
            // For total/netNew tabs, use getLeadsV2 API
            const params = new URLSearchParams();
            params.set('tab', activeSubTab || 'total');
            params.set('_nr', '1');
            res = await getLeadsV2(params.toString(), payload);
          }

          // Store the last response to use when polling stops
          lastPollingResponseRef.current = res;

          // Extract profiles from response
          const resolvedProfiles = Array.isArray(res?.payload)
            ? res?.payload
            : res?.payload?.profiles || [];

          if (resolvedProfiles?.length) {
            // Check if there are any revealing leads in the current response
            const hasRevealingLeads = resolvedProfiles.some(
              (lead) => lead.isRevealing,
            );
            hasRevealingLeadsRef.current = hasRevealingLeads;

            // VALIDATION: Check if the response matches current page/filters before updating
            const currentFiltersPayload = handleGeneratePayload();
            const validationCurrentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch =
              validationCurrentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            // Only update leads data if we're still on the same page and filters
            if (!isPageMatch || !isFilterMatch) {
              // Don't update the data, but keep the response for potential use
              return;
            }

            // Update revealing leads state based on isRevealing status from response
            const revealingEmailLeads = resolvedProfiles.filter(
              (lead) =>
                lead.isRevealing && lead?.revealType === LeadRevealType.EMAIL,
            );
            const revealingEmailPhoneLeads = resolvedProfiles.filter(
              (lead) =>
                lead.isRevealing &&
                lead?.revealType === LeadRevealType.EMAIL_PHONE,
            );
            const revealedLeads = resolvedProfiles.filter(
              (lead) => lead.isRevealed && lead?.isRevealing === false,
            );

            // Update email revealing leads state
            const updateLoadingEmailLeads: [number, boolean][] = [];
            updateLoadingEmailLeads.push(
              ...revealingEmailLeads.map((lead) => [lead.id, true]),
            );
            updateLoadingEmailLeads.push(
              ...revealedLeads
                .filter((lead) => lead?.revealType === LeadRevealType.EMAIL)
                .map((lead) => [lead.id, false]),
            );
            setEmailRevealingLeads((prev) => {
              // Preserve any leads that are currently revealing but not in the API response
              // This handles the case where a lead was just revealed but isn't on the current page
              const preservedRevealingLeads = Object.entries(prev)
                .filter(
                  ([id, isRevealing]) =>
                    isRevealing &&
                    !updateLoadingEmailLeads.some(
                      ([updateId]) => String(updateId) === String(id),
                    ),
                )
                .map(([id]) => [Number(id), true] as [number, boolean]);

              const allUpdates = [
                ...updateLoadingEmailLeads,
                ...preservedRevealingLeads,
              ];
              return {
                ...prev,
                ...Object.fromEntries(allUpdates),
              };
            });

            // Update email phone revealing leads state
            const updateLoadingEmailPhoneLeads: [number, boolean][] = [];
            updateLoadingEmailPhoneLeads.push(
              ...revealingEmailPhoneLeads.map((lead) => [lead.id, true]),
            );
            updateLoadingEmailPhoneLeads.push(
              ...revealedLeads
                .filter(
                  (lead) => lead?.revealType === LeadRevealType.EMAIL_PHONE,
                )
                .map((lead) => [lead.id, false]),
            );
            setEmailPhoneRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(updateLoadingEmailPhoneLeads),
            }));

            // Update leadsData during polling to reflect latest isRevealing/isRevealed status
            if (isSaved) {
              const savedPayload = res?.payload || {};
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: savedPagination,
              });
            } else {
              // Use ref to get latest totalRows to avoid stale closure
              const totalRows =
                leadsDataRef.current?.pagination?.totalRows || 0;

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: {
                  page: validationCurrentPage,
                  limit: LEADS_PER_PAGE,
                  totalRows,
                },
              });
            }
          } else {
            hasRevealingLeadsRef.current = false;
          }
        }

        // Enable polling if requested
        // For newPolling: enable after API call completes (so ref is updated)
        // For enablePolling without newPolling: enable immediately (used by interval)
        if (enablePolling) {
          setIsPollingEnabled(true);
        }
      } catch (e: any) {
        toaster.error(e?.message || SystemErrors.InternalServerError);
      }
    },
    [
      handleGeneratePayload,
      activeSubTab,
      apiParam,
      savedApiParam,
      setLeadsData,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setIsPollingEnabled,
    ],
  );

  // Auto-start polling when search completes and leadsData contains any revealing leads
  useEffect(() => {
    // Only check when search completes (isLoading transitions from true to false)
    const searchJustCompleted =
      previousIsLoadingRef.current === true && isLoading === false;
    previousIsLoadingRef.current = isLoading;

    // Skip if search hasn't completed or if still loading
    if (!searchJustCompleted || isLoading) {
      return;
    }

    // Skip if no leads data
    const profiles = leadsData?.profiles || [];
    if (profiles.length === 0) {
      return;
    }

    // Get current page and filters to create a unique key
    const filtersPayload = handleGeneratePayload();
    const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
    const currentPage = isSaved
      ? Math.ceil(
          (savedApiParam?.start || 1) /
            (savedApiParam?.take || getV2SavedTabPageSize()),
        )
      : apiParam?.page ?? 1;

    const currentFiltersStr = JSON.stringify(filtersPayload);
    const combinationKey = `${currentPage}:${currentFiltersStr}:${activeSubTab}`;

    // Skip if we already processed this exact page/filter combination
    if (processedCombinationsRef.current.has(combinationKey)) {
      return;
    }

    // Mark this combination as processed
    processedCombinationsRef.current.add(combinationKey);

    // Check if leadsData has any revealing leads
    const hasRevealingLeads = profiles.some(
      (lead) => lead.isRevealing === true,
    );

    if (hasRevealingLeads) {
      // Update the ref to indicate we have revealing leads
      hasRevealingLeadsRef.current = true;

      const isPageMatch = pollingPageRef.current === currentPage;
      const isFilterMatch = pollingFiltersRef.current === currentFiltersStr;

      // Start polling if not already polling for this page/filter combination
      // and not already in the process of starting polling
      if (
        (!isPollingEnabled || !isPageMatch || !isFilterMatch) &&
        !isStartingPollingRef.current
      ) {
        // Set flag to prevent multiple simultaneous starts
        isStartingPollingRef.current = true;

        // Reset polling attempts for new polling session
        pollingAttemptsRef.current = 0;
        setIsFirstPollRequest(true);

        // Start polling by calling handleFetchLeadsAfterBulkActions
        handleFetchLeadsAfterBulkActions(true, true).catch(() => {
          // Reset flag if polling start fails
          isStartingPollingRef.current = false;
        });
      }
      return;
    }

    // Update ref if no revealing leads found
    // Only update if we're not currently polling (to avoid stopping active polling)
    if (!isPollingEnabled) {
      hasRevealingLeadsRef.current = false;
    }
  }, [
    isLoading,
    leadsData,
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    handleFetchLeadsAfterBulkActions,
  ]);

  // Reset starting polling flag when polling actually starts
  useEffect(() => {
    if (isPollingEnabled) {
      isStartingPollingRef.current = false;
    }
  }, [isPollingEnabled]);

  // Polling effect
  useEffect(() => {
    const startPolling = () => {
      // Don't start a new interval if one is already running
      if (timerIdRef.current) {
        return;
      }

      // Reset polling attempts only when starting a new polling session
      pollingAttemptsRef.current = 0;

      timerIdRef.current = setInterval(async () => {
        pollingAttemptsRef.current += 1;

        const shouldContinuePolling = hasRevealingLeadsRef.current;

        // Stop if no revealing leads or if we've reached the max attempts (20)
        if (
          !shouldContinuePolling ||
          pollingAttemptsRef.current >= PEOPLE_MAX_POLLING_LIMIT
        ) {
          // Stop polling
          setIsPollingEnabled(false);
          setEmailRevealingLeads({});
          setEmailPhoneRevealingLeads({});
          setIsSelectedAllLeads(false);
          setSelectedLeads([]);
          setDeSelectedLeads([]);
          setSelectAllRevealType(null);
          hasRevealingLeadsRef.current = false;
          pollingAttemptsRef.current = 0;

          // Update leadsData directly from last polling response (no need for another API call)
          if (lastPollingResponseRef.current) {
            const res = lastPollingResponseRef.current;
            const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;

            // Validate before final update
            const currentFiltersPayload = handleGeneratePayload();
            const currentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch = currentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            // Extract profiles from last polling response (same logic as during polling)
            const resolvedProfiles = Array.isArray(res?.payload)
              ? res?.payload
              : res?.payload?.profiles || [];

            if (isPageMatch && isFilterMatch && isSaved) {
              const savedPayload = res?.payload || {};
              // Use resolvedProfiles for consistency with polling logic
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: savedPagination,
              });
            } else if (isPageMatch && isFilterMatch) {
              // Use ref to get latest totalRows to avoid stale closure
              const totalRows =
                leadsDataRef.current?.pagination?.totalRows || 0;

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: {
                  page: currentPage,
                  limit: LEADS_PER_PAGE,
                  totalRows,
                },
              });
            }

            if (isPageMatch && isFilterMatch) {
              dispatch(getPostLoadMetaRequest());
            }

            // Reset polling context
            pollingPageRef.current = null;
            pollingFiltersRef.current = null;
            lastPollingResponseRef.current = null;
          }
        } else {
          await handleFetchLeadsAfterBulkActions(false, true);
          if (isFirstPollRequest) {
            setIsFirstPollRequest(false);
          }
        }
      }, BULK_ACTION_TIMEOUT);
    };

    const stopPolling = () => {
      setIsFirstPollRequest(true);
      pollingAttemptsRef.current = 0;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };

    if (isPollingEnabled) {
      // Only start polling if not already running
      if (!timerIdRef.current) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => {
      // Only stop polling if isPollingEnabled becomes false
      if (!isPollingEnabled) {
        stopPolling();
      }
    };
  }, [
    isPollingEnabled,
    isFirstPollRequest,
    activeSubTab,
    savedApiParam,
    apiParam,
  ]);

  // Single lead reveal
  const handleRevealLead = useCallback(
    async (leadId: number, revealType: LeadRevealType) => {
      try {
        setLeadFinderRevealType(revealType);

        const payload = {
          leadIds: [leadId],
          revealType,
          tagIds: [],
          newTags: [],
        };

        const revealRes = await bulkRevealLeadAPI(payload);
        if (revealRes) {
          const { message, success } = revealRes.payload || {};
          if (!success) {
            toaster.error(
              isInsufficientCreditsError(message)
                ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                : message || SystemErrors.InternalServerError,
              {
                theme: Theme.New,
              },
            );
            // Clear loading state on error
            if (revealType === LeadRevealType.EMAIL) {
              setEmailRevealingLeads((prev) => {
                const updated = { ...prev };
                delete updated[leadId];
                return updated;
              });
            }
            if (revealType === LeadRevealType.EMAIL_PHONE) {
              setEmailPhoneRevealingLeads((prev) => {
                const updated = { ...prev };
                delete updated[leadId];
                return updated;
              });
            }
          }
          if (success) {
            toaster.success(message || '1 Lead will be revealed', {
              theme: Theme.New,
            });
            hasRevealingLeadsRef.current = true;
            // Ensure the lead is in the revealing state before starting polling
            // This is a safeguard in case the state update from handleRevealEmail hasn't committed yet
            if (revealType === LeadRevealType.EMAIL) {
              setEmailRevealingLeads((prev) => ({
                ...prev,
                [leadId]: true,
              }));
            }
            if (revealType === LeadRevealType.EMAIL_PHONE) {
              setEmailPhoneRevealingLeads((prev) => ({
                ...prev,
                [leadId]: true,
              }));
            }
            // Use setTimeout to ensure state update is committed before starting polling
            setTimeout(() => {
              handleFetchLeadsAfterBulkActions(true, true);
            }, 0);
          }
        }
      } catch (e: any) {
        if (revealType === LeadRevealType.EMAIL) {
          setEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            delete updated[leadId];
            return updated;
          });
        }
        if (revealType === LeadRevealType.EMAIL_PHONE) {
          setEmailPhoneRevealingLeads((prev) => {
            const updated = { ...prev };
            delete updated[leadId];
            return updated;
          });
        }
        const backendMessage = e?.message;
        toaster.error(
          isInsufficientCreditsError(backendMessage)
            ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
            : backendMessage || SystemErrors.InternalServerError,
          {
            theme: Theme.New,
          },
        );
      }
    },
    [
      handleFetchLeadsAfterBulkActions,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setLeadFinderRevealType,
    ],
  );

  // Bulk reveal
  const handleBulkReveal = useCallback(
    async (
      revealType: LeadRevealType,
      tagIds: number[] = [],
      newTags: string[] = [],
    ) => {
      try {
        setLeadFinderRevealType(revealType);

        if (isSelectedAllLeads) {
          // Set selectAllRevealType immediately before API call
          setSelectAllRevealType(revealType);

          const generatedPayload = handleGeneratePayload();
          const leadsFilterPayload =
            generatedPayload && typeof generatedPayload === 'object'
              ? generatedPayload
              : {};

          const totalRows = leadsData?.pagination?.totalRows || 0;
          const maxLimit =
            activeSubTab === LeadFinderSecondaryTab.SAVED
              ? SAVED_TAB_BULK_OPERATION_LIMIT
              : leadFinderLimit;
          const baseCount = Math.min(maxLimit, totalRows);

          const payload: any = {
            leadsFilter: {
              ...leadsFilterPayload,
              tab: activeSubTab,
              page: totalRows > leadFinderLimit ? 1 : apiParam?.page,
            },
            revealType,
            tagIds: tagIds?.length > 0 ? tagIds : [],
            newTags: newTags?.length > 0 ? newTags : [],
            leadsToBeRevealedCount: baseCount,
          };

          // Add optional fields only if they have values
          if (deSelectedLeads && deSelectedLeads.length > 0) {
            payload.deSelectedLeadIds = deSelectedLeads;
          }

          const revealRes = await bulkRevealLeadAPI(payload);
          if (revealRes) {
            const { message, status } = revealRes.payload || {};
            if (status === 0) {
              toaster.error(
                isInsufficientCreditsError(message)
                  ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                  : message || SystemErrors.InternalServerError,
                {
                  theme: Theme.New,
                },
              );
              setSelectAllRevealType(null);
            } else if (status === 2) {
              toaster.warning(
                message ||
                  'Your another bulk lead reveal action is in progress. Please wait until it finishes',
                { theme: Theme.New },
              );
              setSelectAllRevealType(null);
            } else {
              if (revealRes?.payload?.shouldPoll) {
                dispatch(enableBulkActionRequestPolling());
              }
              toaster.success(
                message ||
                  'Bulk reveal for leads are started. This can take few moments, You will be notified once the process is completed.',
                { theme: Theme.New },
              );
              // Set ref to true initially since we just triggered reveal
              hasRevealingLeadsRef.current = true;
              setTimeout(() => {
                handleFetchLeadsAfterBulkActions(true, true);
              }, 0);
            }
          }
        } else {
          // For individual selection, use leadIds
          const leadIds: number[] = selectedLeads ?? [];

          // Set loading states
          if (revealType === LeadRevealType.EMAIL) {
            setEmailRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(leadIds.map((lead) => [lead, true])),
            }));
          }
          if (revealType === LeadRevealType.EMAIL_PHONE) {
            setEmailPhoneRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(leadIds.map((lead) => [lead, true])),
            }));
          }

          const payload = {
            leadIds,
            revealType,
            tagIds: tagIds.length > 0 ? tagIds : [],
            newTags: newTags.length > 0 ? newTags : [],
          };

          const revealRes = await bulkRevealLeadAPI(payload);
          if (revealRes) {
            const { message, status } = revealRes.payload || {};
            if (status === 0) {
              toaster.error(
                isInsufficientCreditsError(message)
                  ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                  : message || SystemErrors.InternalServerError,
                {
                  theme: Theme.New,
                },
              );
              // Clear loading states on error
              if (revealType === LeadRevealType.EMAIL) {
                setEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.EMAIL_PHONE) {
                setEmailPhoneRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
            } else if (status === 2) {
              toaster.warning(
                message ||
                  'Your another bulk lead reveal action is in progress. Please wait until it finishes',
                { theme: Theme.New },
              );
              // Clear loading states on warning
              if (revealType === LeadRevealType.EMAIL) {
                setEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.EMAIL_PHONE) {
                setEmailPhoneRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
            } else {
              if (revealRes?.payload?.shouldPoll) {
                dispatch(enableBulkActionRequestPolling());
              }
              toaster.success(
                message ||
                  'Bulk reveal for leads are started. This can take few moments, You will be notified once the process is completed.',
                { theme: Theme.New },
              );
              // Set ref to true initially since we just triggered reveal
              hasRevealingLeadsRef.current = true;
              handleFetchLeadsAfterBulkActions(true, true);
            }
          }
        }

        // Clear selection after reveal
        setIsSelectedAllLeads(false);
        setSelectAllRevealType(null);
        setSelectedLeads([]);
        setDeSelectedLeads([]);
      } catch (e: any) {
        const backendMessage = e?.message;
        toaster.error(
          isInsufficientCreditsError(backendMessage)
            ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
            : backendMessage || SystemErrors.InternalServerError,
          {
            theme: Theme.New,
          },
        );
        // Clear loading states on error
        if (revealType === LeadRevealType.EMAIL) {
          setEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            selectedLeads.forEach((id) => {
              delete updated[id];
            });
            return updated;
          });
        }
        if (revealType === LeadRevealType.EMAIL_PHONE) {
          setEmailPhoneRevealingLeads((prev) => {
            const updated = { ...prev };
            selectedLeads.forEach((id) => {
              delete updated[id];
            });
            return updated;
          });
        }
      }
    },
    [
      isSelectedAllLeads,
      deSelectedLeads,
      selectedLeads,
      leadsData,
      handleGeneratePayload,
      handleFetchLeadsAfterBulkActions,
      dispatch,
      setIsSelectedAllLeads,
      setSelectedLeads,
      setDeSelectedLeads,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setLeadFinderRevealType,
      setSelectAllRevealType,
      activeSubTab,
      leadFinderLimit,
    ],
  );

  const refreshLeadsAndStartPolling = useCallback(() => {
    hasRevealingLeadsRef.current = true;
    handleFetchLeadsAfterBulkActions(true, true);
  }, [handleFetchLeadsAfterBulkActions]);

  return {
    handleRevealLead,
    handleBulkReveal,
    refreshLeadsAndStartPolling,
    isPollingEnabled,
    leadFinderRevealType,
    setLeadFinderRevealType,
    selectAllRevealType,
  };
};
